/**
 * Homepage hero carousel.
 *
 * Image slides hold for the section's interval. A video slide plays its clip
 * once from the first frame and moves on when the clip ends, so the progress
 * line follows the clip itself: a buffering clip holds its slide rather than
 * being cut short by a clock. A clip that cannot play (autoplay blocked, as in
 * iOS Low Power Mode, or a failed source) leaves its first frame showing and
 * the slide times like an image; one that stops making progress is skipped.
 *
 * Rotation pauses off screen, in a background tab, with the pause button, and
 * starts paused under reduced motion. Hovering a link or control, or keyboard
 * focus inside, keeps the current slide: an image stops its clock, a clip plays
 * to its end and the slide changes once the pointer or focus leaves.
 */
if (!customElements.get('haus-editorial-hero')) {
  const MAX_CLIP = 30000;

  customElements.define('haus-editorial-hero', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      this.slides = [...this.querySelectorAll('.haus-hero__slide')];
      this.dots = [...this.querySelectorAll('[data-slide]')];
      this.toggle = this.querySelector('[data-pause]');
      this.track = this.querySelector('.haus-hero__slides');
      this.interval = Number(this.dataset.interval) || 7000;
      this.stallLimit = Math.max(this.interval, 5000);
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.reduced.matches;
      this.visible = true;
      this.pointerHold = false;
      this.focusHold = false;
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.tick = this.tick.bind(this);

      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = this.slides.length < 2;
        const fail = () => {
          slide.clipFailed = true;
          if (i === this.index) this.restartClock();
        };
        on(video, 'playing', () => video.classList.add('is-ready'));
        on(video, 'ended', () => { if (i === this.index) this.clipEnded(); });
        on(video, 'error', fail);
        const sources = video.querySelectorAll('source');
        if (sources.length) on(sources[sources.length - 1], 'error', fail);
        // A source that failed before this script ran has already fired its error event.
        if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) slide.clipFailed = true;
      });

      this.dots.forEach((dot, i) => on(dot, 'click', () => this.show(i)));
      if (this.toggle) on(this.toggle, 'click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
      });
      on(this, 'pointerover', event => {
        if (event.pointerType !== 'mouse') return;
        this.setPointerHold(Boolean(event.target.closest('.haus-hero__look, .haus-hero__button, .haus-hero__controls')));
      });
      on(this, 'pointerleave', () => this.setPointerHold(false));
      on(this, 'focusin', event => {
        this.focusHold = event.target !== this.toggle && event.target.matches(':focus-visible');
        this.updatePlayback();
      });
      on(this, 'focusout', event => {
        if (this.contains(event.relatedTarget)) return;
        this.focusHold = false;
        this.updatePlayback();
      });
      on(this, 'keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        this.dots[this.index]?.focus();
      });
      on(this, 'touchstart', event => {
        this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { passive: true });
      on(this, 'touchend', event => {
        if (!this.touch) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
        this.touch = null;
      }, { passive: true });
      on(document, 'visibilitychange', () => this.updatePlayback());
      on(window, 'pageshow', () => this.updatePlayback());
      on(this.reduced, 'change', () => {
        if (this.reduced.matches) this.paused = true;
        this.updatePlayback();
      });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.paused = true;
        this.show(index);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayback();
      }, { threshold: .1 });
      this.observer.observe(this);
      this.show(this.index);
    }

    running() {
      return !this.paused && this.visible && !document.hidden && this.slides.length > 0;
    }

    held() {
      return this.pointerHold || this.focusHold;
    }

    /** Releases after a short grace period, so crossing the gap between two links does not let the slide change. */
    setPointerHold(hold) {
      clearTimeout(this.release);
      if (hold === this.pointerHold) return;
      if (hold) {
        this.pointerHold = true;
        this.updatePlayback();
        return;
      }
      this.release = setTimeout(() => {
        this.pointerHold = false;
        this.updatePlayback();
      }, 400);
    }

    /** The active slide's clip, unless it cannot play and the slide is timed like an image. */
    clip() {
      const slide = this.slides[this.index];
      const video = slide?.querySelector('video');
      return video && !slide.clipFailed && !slide.clipBlocked ? video : null;
    }

    restartClock() {
      this.elapsed = 0;
      this.setProgress(0);
    }

    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      const current = this.slides[this.index];
      this.elapsed = 0;
      this.stalled = 0;
      this.clipTime = -1;
      this.pendingAdvance = false;
      current.clipBlocked = false;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        this.dots[i]?.classList.toggle('is-active', active);
        this.dots[i]?.style.setProperty('--progress', 0);
        if (active) this.dots[i]?.setAttribute('aria-current', 'true');
        else this.dots[i]?.removeAttribute('aria-current');
        const video = slide.querySelector('video');
        if (!video || active) return;
        video.pause();
        clearTimeout(slide.rewind);
        slide.rewind = setTimeout(() => {
          if (!slide.classList.contains('is-active') && video.currentTime > 0) video.currentTime = 0;
        }, 1000);
      });
      const video = current.querySelector('video');
      if (video) {
        clearTimeout(current.rewind);
        if (video.currentTime > 0) video.currentTime = 0;
      }
      this.warm(current);
      this.warm(this.slides[(this.index + 1) % this.slides.length]);
      this.dataset.ink = current.dataset.ink || 'light';
      this.dataset.inkCompact = current.dataset.inkCompact || this.dataset.ink;
      this.updatePlayback();
    }

    /** Let a clip that was left unloaded start buffering before its slide comes round. */
    warm(slide) {
      const video = slide?.querySelector('video');
      if (!video || video.preload !== 'none' || slide.clipFailed) return;
      video.preload = 'auto';
      video.load();
    }

    clipEnded() {
      if (this.slides.length < 2) return;
      this.setProgress(1);
      if (!this.running() || this.held()) {
        this.pendingAdvance = true;
        return;
      }
      this.show(this.index + 1);
    }

    updatePlayback() {
      const running = this.running();
      if (running && !this.held() && this.pendingAdvance) {
        this.show(this.index + 1);
        return;
      }
      this.classList.toggle('is-paused', !running || this.held());
      this.track?.setAttribute('aria-live', running ? 'off' : 'polite');
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
        this.toggle.dataset.paused = String(this.paused);
      }
      const video = this.clip();
      if (video) {
        if (running) this.play(video);
        else video.pause();
      }
      if (running && !this.frame) {
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    play(video) {
      if (!video.paused || video.ended) return;
      const slide = this.slides[this.index];
      video.play()?.catch(error => {
        // AbortError only means a pause() or slide change came first.
        if (error.name !== 'NotAllowedError' && error.name !== 'NotSupportedError') return;
        if (slide !== this.slides[this.index]) return;
        slide.clipBlocked = true;
        this.restartClock();
      });
    }

    tick(now) {
      const step = Math.min(now - this.lastTime, 200);
      this.lastTime = now;
      if (!this.running()) {
        this.frame = 0;
        return;
      }
      const video = this.clip();
      if (video) {
        const time = video.currentTime * 1000;
        const length = Math.min(video.duration * 1000 || Infinity, MAX_CLIP);
        if (Number.isFinite(length)) this.setProgress(Math.min(time / length, 1));
        if (time > this.clipTime || video.ended) this.stalled = 0;
        else this.stalled += step;
        this.clipTime = time;
        if (time >= MAX_CLIP && !video.ended) {
          video.pause();
          this.clipEnded();
        } else if (this.stalled >= this.stallLimit && this.slides.length > 1 && !this.held()) {
          this.show(this.index + 1);
        }
      } else if (this.slides.length > 1) {
        if (!this.held()) this.elapsed += step;
        this.setProgress(Math.min(this.elapsed / this.interval, 1));
        if (this.elapsed >= this.interval) this.show(this.index + 1);
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    setProgress(value) {
      this.dots[this.index]?.style.setProperty('--progress', value);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.release);
      this.frame = 0;
      this.abort.abort();
      this.observer.disconnect();
      this.slides.forEach(slide => {
        clearTimeout(slide.rewind);
        slide.querySelector('video')?.pause();
      });
    }
  });
}
